/**
 * Persists the session's join code per (businessId, tableNumber) so a
 * re-scan or reload of /dine-in/:businessId/:tableNumber doesn't ask an
 * already-joined member to re-enter it.
 *
 * DineIn.tsx reads this before firing the openTable bootstrap call;
 * DineInDb writes back whatever code the backend confirms on every
 * successful openTable/joinTable response, and clears it if a stored code
 * turns out to be stale (backend rejects it with INVALID_OR_MISSING_SESSION_CODE).
 *
 * Lives in services/helpers (not the dineIn page folder) because the Redux
 * slice writes to it directly — a data-layer file reaching into
 * components/pages would invert the repo's usual layering.
 */

const STORAGE_KEY_PREFIX = "nine_dinein_code_";

function storageKey(businessId: string, tableNumber: string): string {
  return `${STORAGE_KEY_PREFIX}${businessId}_${tableNumber}`;
}

export function getStoredDineInCode(
  businessId: string,
  tableNumber: string
): string | null {
  try {
    return localStorage.getItem(storageKey(businessId, tableNumber));
  } catch {
    // Storage unavailable (private browsing, quota) — treat as "no code on file".
    return null;
  }
}

export function setStoredDineInCode(
  businessId: string,
  tableNumber: string,
  sessionCode: string
): void {
  try {
    localStorage.setItem(storageKey(businessId, tableNumber), sessionCode);
  } catch {
    // Non-fatal — the session still works, it'll just re-prompt for the code next visit.
  }
}

export function clearStoredDineInCode(businessId: string, tableNumber: string): void {
  try {
    localStorage.removeItem(storageKey(businessId, tableNumber));
  } catch {
    // no-op
  }
}

/**
 * Persists which single dine-in session (if any) the user is currently an
 * active participant in — a global marker, not per-table like the code
 * above, because pages outside /dine-in/:businessId/:tableNumber (e.g. the
 * Products tab) don't have a tableNumber to key off, and a person can only
 * meaningfully be "at" one table at a time.
 *
 * Set on every successful openTable/joinTable/submitRound/addDineInItems
 * response (applySessionResponse in DineInDb); cleared once the backend
 * confirms the session is gone or the user's no longer part of it
 * (SESSION_NOT_FOUND / NOT_A_SESSION_MEMBER). Lets other pages answer "is
 * there an active dine-in session for business X, and where do I send
 * someone back to" without a network round-trip.
 */
export interface ActiveDineInSession {
  businessId: string;
  tableNumber: string;
  /** == DineInSession.sessionId — named to match AddDineInItemPayload.dineInId. */
  dineInId: string;
}

const ACTIVE_SESSION_KEY = "nine_dinein_active_session";

export function getActiveDineInSession(): ActiveDineInSession | null {
  try {
    const raw = localStorage.getItem(ACTIVE_SESSION_KEY);
    return raw ? (JSON.parse(raw) as ActiveDineInSession) : null;
  } catch {
    return null;
  }
}

export function setActiveDineInSession(session: ActiveDineInSession): void {
  try {
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Non-fatal — the Products page just won't offer "add to dine-in session".
  }
}

export function clearActiveDineInSession(): void {
  try {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch {
    // no-op
  }
}
